import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { createBaseBrokeragePlan, getActiveProjects } from "../../Services/Brokerage";
import { errorTost, successTost } from "../../Services/Toast";

const steps = [
  { label: "Project Scope", sub: "Select projects" },
  { label: "Brokerage Type", sub: "Choose calculation" },
  { label: "Status", sub: "Set status" },
  { label: "Review", sub: "Confirm & save" },
];

const Row = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" py={1.5}>
    <Typography fontSize={14} color="text.secondary">
      {label}
    </Typography>
    <Typography fontSize={14} fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

const CreateBasicBrokeragePlanStep4 = ({
  data,
  onBack,
  onSubmit,
}) => {
  const {
    planName,
    scope,
    selectedProject,
    selectedProjects,
    brokerageType,
    percentage,
    fixedAmount,
    startDate,
    endDate,
    isActive,
  } = data;

  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await getActiveProjects();
        setProjects(res);
      } catch (err) {
        console.error(err);
      }
    };
    loadProjects();
  }, []);

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.projectId === projectId);
    return project ? project.projectName : projectId;
  };

  const projectLabel =
    scope === "single"
      ? getProjectName(selectedProject)
      : selectedProjects.map(id => getProjectName(id)).join(", ");



  const brokerageValue =
    brokerageType === "percentage"
      ? `${percentage}%`
      : `₹${fixedAmount} L`;

  const validityLabel =
    startDate || endDate
      ? `${startDate || "—"} to ${endDate || "—"}`
      : "Always Active";
  const submitLock = React.useRef(false);
  const handleSubmitPlan = () => {
    onSubmit();
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>



      {/* BACK BUTTON */}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: "none", color: "#2563EB" }}
          onClick={onBack}
        >
          Back to Plans
        </Button>
      </Box>

      {/* STEPPER */}
      <Stepper
        activeStep={3}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepIcon-root": { fontSize: "3rem" },
          "& .MuiStepIcon-root.Mui-active": { color: "#2563EB" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#2563EB" },
          "& .MuiStepLabel-label": {
            fontSize: "15px",
            fontWeight: 600,
            mt: 1,
          },
          "& .MuiStepConnector-line": {
            borderTopWidth: 3,
            borderColor: "#E5E7EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography fontWeight={600}>{step.label}</Typography>
              <Typography fontSize={13} color="text.secondary">
                {step.sub}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* MAIN GRID */}
      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={4}>

        {/* LEFT SECTION */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 4,
            border: "1px solid #E5E7EB",
          }}
        >
          <Box display="flex" gap={1.5} alignItems="center" mb={3}>
            <AutoAwesomeIcon sx={{ color: "#2563EB" }} />
            <Typography fontSize={18} fontWeight={600}>
              Review Configuration
            </Typography>
          </Box>

          <Row label="Plan Name" value={planName} />
          <Divider />
          <Row label="Projects" value={projectLabel} />
          <Divider />
          <Row
            label="Calculation Type"
            value={
              brokerageType === "percentage"
                ? "Percentage"
                : "Fixed Amount"
            }
          />
          <Divider />
          <Row label="Value" value={brokerageValue} />
          <Divider />
          <Row label="Validity" value={validityLabel} />
          <Divider />

          <Box display="flex" justifyContent="space-between" py={1.5}>
            <Typography fontSize={14} color="text.secondary">
              Status
            </Typography>
            <Chip
              label={isActive ? "Active" : "Inactive"}
              size="small"
              sx={{
                background: isActive ? "#DCFCE7" : "#FEE2E2",
                color: isActive ? "#15803D" : "#991B1B",
                fontWeight: 500,
              }}
            />
          </Box>

          {/* READY BOX */}
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 3,
              background: "#ECFDF5",
              border: "1px solid #86EFAC",
            }}
          >
            <Box display="flex" gap={1} alignItems="center">
              <CheckCircleIcon sx={{ color: "#16A34A" }} />
              <Typography fontWeight={600} color="#15803D">
                Ready to Activate
              </Typography>
            </Box>

            <Typography fontSize={13} color="#166534" mt={1}>
              This configuration will apply to all new agreements immediately after activation.
            </Typography>
          </Box>
        </Box>

        {/* RIGHT SECTION */}
        <Box display="flex" flexDirection="column" gap={3}>

          {/* LIVE PREVIEW */}
          {/* <Box
            sx={{
              background: "#fff",
              borderRadius: 3,
              p: 3,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography fontWeight={600} mb={2}>
              Live Preview
            </Typography>

            <Typography fontSize={14}>
              Agreement Value: <b>₹5.00 Cr</b>
            </Typography>

            <Typography fontSize={14}>
              Base Brokerage: <b>{brokerageValue}</b>
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={600} fontSize={16}>
              Brokerage: {brokerageValue}
            </Typography>
          </Box> */}

          {/* BUSINESS RULES */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: 3,
              p: 3,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography fontWeight={600} mb={2}>
              Business Rules
            </Typography>

            {[
              "Calculated after agreement completion",
              "Paid immediately",
              "Cannot be overridden",
              "Always shown on CP dashboard",
            ].map((rule) => (
              <Box key={rule} display="flex" gap={1.5} mb={1.5}>
                <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                <Typography fontSize={13}>{rule}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box display="flex" justifyContent="space-between" mt={6}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitPlan}
        >
          Activate Plan
        </Button>
      </Box>
    </Box>
  );
};

export default CreateBasicBrokeragePlanStep4;